/**
 * Pageviews for the pages the shell serves itself.
 *
 * The shell tracks its own pages only. A remote's pages stay the remote's to
 * report — the shell would otherwise have to hold a view name for every page of
 * every remote, which makes each new remote page a shell change.
 *
 * Mounted once, in `App`, above the surface switch: `main.tsx` puts the router
 * around all of `App`, so one mount covers both the Forge and identity origins.
 */

import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {activeRouteClaim, shellRouteClaimTable} from '../routeClaims';
import {SHELL_PAGES} from '../shellPages';
import type {Surface} from '../surface';
import {analyticsForShell} from './segment';

/** Where a path matches no shell page and no remote claims it. */
const NOT_FOUND: Record<Surface, string> = {
  forge: 'shell-not-found',
  identity: 'identity-not-found',
};

/**
 * React Router matches `/login/` to `path="/login"`, so the two are one page.
 * Applied before anything else reads the path, so that the view name and the
 * already-reported check agree on what counts as the same location.
 */
function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

/** Takes an already-normalized path. */
function routeNameFor(surface: Surface, path: string): string {
  return SHELL_PAGES[surface][path] ?? NOT_FOUND[surface];
}

export function usePageViews({surface}: {surface: Surface}): void {
  const {pathname, search, hash} = useLocation();
  /*
    The last location reported, so that a location arrived at twice in a row
    sends one event. The effect's dependencies already cover ordinary
    re-renders; this covers `StrictMode` running the effect twice on mount, and
    it holds across a claimed path — `/` -> a remote page -> `/` sends two
    events, because the middle location is recorded even though the shell sends
    nothing for it.
  */
  const sent = useRef<string | undefined>(undefined);

  useEffect(() => {
    const path = normalizePathname(pathname);
    const location = `${path}${search}${hash}`;
    if (sent.current === location) {
      return;
    }
    sent.current = location;

    /*
      A path a remote serves is that remote's pageview to report, through its
      own route tracking. The claim table is module-cached, so asking again here
      costs nothing. Identity has no claims: every path on that origin is the
      shell's.
    */
    if (
      surface === 'forge' &&
      activeRouteClaim(shellRouteClaimTable(), path) != null
    ) {
      return;
    }

    /*
      Only what Segment cannot work out for itself. It fills in the url, path,
      search, referrer, title and campaign parameters on every `page` call, and
      our own copies of those would be a second answer that eventually disagrees
      with its. `app_name` and the host session id are added inside
      `analyticsForShell`.
    */
    analyticsForShell.page({
      routeName: routeNameFor(surface, path),
      surface,
    });
  }, [surface, pathname, search, hash]);
}
